import { useState } from "react";
import {
  X, Plus, Trash2, Check, ListChecks, AlertTriangle, Package, FileText, PencilLine,
  Settings2, Clock, PenTool, User2, Eye, EyeOff,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import CustomFieldsDialog from "@/components/CustomFieldsDialog";

/**
 * TemplateEditorDialog — työpohjan rakennus.
 *
 * Suunnitteluperiaatteet (Iter 50):
 *  - "Rakennan työpakettia" -tunne, ei "täytän lomaketta".
 *  - Tyhjät input-kentät piilossa oletuksena. Näkyvillä on lista + "+ Lisää" -nappi.
 *  - Lisätyt itemit näkyvät klikattavina listariveinä, ei input-kenttinä.
 *  - Kunkin osion otsikossa progress: "✓ 3 työvaihetta" tai "Ei vielä".
 *  - Ilmaa, väljyyttä, kevyt värikoodaus ikoneilla.
 */
export default function TemplateEditorDialog({ template, onClose, onSaved }) {
  const isNew = !template?.id;
  const [name, setName] = useState(template?.name || "");
  const [workItems, setWorkItems] = useState(() => {
    const findings = template?.base_checklist_findings || [];
    return (template?.base_checklist || []).filter((s) => !findings.includes(s));
  });
  const [findingItems, setFindingItems] = useState(() => [...(template?.base_checklist_findings || [])]);
  const [materials, setMaterials] = useState(() =>
    (template?.materials || []).map((m) => ({ name: m.name || "", unit: m.unit || "kpl" })),
  );
  const [customFields, setCustomFields] = useState(() => [...(template?.custom_fields || [])]);
  const [customEditorOpen, setCustomEditorOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  // Raporttiasetukset — periytyvät työpohjasta uuteen raporttiin.
  const [settings, setSettings] = useState(() => ({
    image_timestamps: template?.image_timestamps ?? true,
    request_signature: template?.request_signature ?? true,
    ask_executor: template?.ask_executor ?? false,
    hide_work_checklists: template?.hide_work_checklists ?? false,
    hide_finding_checklists: template?.hide_finding_checklists ?? false,
    hide_materials: template?.hide_materials ?? false,
    hide_kpi_cards: template?.hide_kpi_cards ?? true,
  }));

  const save = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      toast.error("Anna työpohjalle nimi");
      return;
    }
    const cleanedWork = workItems.map((s) => s.trim()).filter(Boolean);
    const cleanedFindings = findingItems.map((s) => s.trim()).filter(Boolean);
    const base = [...cleanedWork, ...cleanedFindings.filter((f) => !cleanedWork.includes(f))];
    const cleanedMaterials = materials
      .map((m) => ({ name: (m.name || "").trim(), unit: (m.unit || "kpl").trim() || "kpl" }))
      .filter((m) => m.name);
    setSaving(true);
    try {
      const payload = {
        name: trimmed,
        base_checklist: base,
        base_checklist_findings: cleanedFindings,
        materials: cleanedMaterials,
        custom_fields: customFields,
        ...settings,
      };
      if (isNew) {
        await api.post("/content-templates", payload);
        toast.success(`Työpohja "${trimmed}" tallennettu`);
      } else {
        await api.patch(`/content-templates/${template.id}`, payload);
        toast.success(`Työpohja "${trimmed}" päivitetty`);
      }
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Tallennus epäonnistui");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-6"
      data-testid="template-editor-dialog"
    >
      <div className="bg-slate-50 w-full md:max-w-3xl md:rounded-3xl rounded-t-3xl shadow-2xl flex flex-col max-h-[96vh]">
        {/* Header — nimi näkyvästi otsikkoalueella */}
        <div className="p-6 md:p-8 pb-5 md:pb-6 flex-shrink-0 bg-white md:rounded-t-3xl rounded-t-3xl border-b border-slate-100">
          <div className="flex items-start justify-between mb-4">
            <div className="text-[11px] uppercase tracking-widest text-emerald-600 font-semibold">
              {isNew ? "Uusi työpohja" : "Muokkaa työpohjaa"}
            </div>
            <button
              onClick={onClose}
              className="p-2 -mt-1 -mr-1 rounded-xl hover:bg-slate-100 flex-shrink-0"
              data-testid="template-editor-close"
              aria-label="Sulje"
            >
              <X className="h-5 w-5 text-slate-400" />
            </button>
          </div>
          <label htmlFor="template-name-field" className="block text-sm font-semibold text-slate-700 mb-2">
            Työpohjan nimi <span className="text-rose-500">*</span>
          </label>
          <input
            id="template-name-field"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="esim. IV-vuosihuolto, LVI-tarkastus, Kylpyhuoneremontti…"
            className="w-full px-4 py-3.5 rounded-xl border-2 border-slate-200 focus:border-emerald-500 outline-none text-base md:text-lg font-semibold text-slate-900 placeholder:text-slate-300 placeholder:font-normal placeholder:text-sm bg-white transition-colors"
            autoFocus={isNew}
            data-testid="template-name-input"
          />
          <p className="text-xs md:text-sm text-slate-500 mt-3 leading-relaxed">
            Rakennat asentajalle työpaketin, jota käytätte kaikissa tulevissa saman tyyppisissä töissä.
          </p>
        </div>

        {/* Body — osiot rakennuspalikoina */}
        <div className="flex-1 overflow-y-auto px-4 md:px-6 py-5 md:py-6 space-y-4 md:space-y-5">
          <BuildingBlock
            accent="emerald"
            icon={ListChecks}
            title="Työvaiheet"
            subtitle="Vaiheet, jotka asentaja kuittaa työn aikana."
            emptyLabel="Ei työvaiheita vielä"
            countLabel={(n) => `${n} ${n === 1 ? "työvaihe" : "työvaihetta"}`}
            items={workItems}
            onChange={setWorkItems}
            placeholder="esim. Suodattimet vaihdettu"
            addLabel="Lisää työvaihe"
            testId="work"
          />

          <BuildingBlock
            accent="amber"
            icon={AlertTriangle}
            title="Havaintolistat"
            subtitle="Kohdat, joihin asentaja kirjaa poikkeamia tai huomioita."
            emptyLabel="Ei havaintolistoja vielä"
            countLabel={(n) => `${n} ${n === 1 ? "havainto" : "havaintoa"}`}
            items={findingItems}
            onChange={setFindingItems}
            placeholder="esim. Vesivuoto havaittu"
            addLabel="Lisää havainto"
            testId="finding"
          />

          <MaterialsBlock
            materials={materials}
            onChange={setMaterials}
          />

          <CustomFieldsBlock
            fields={customFields}
            onOpen={() => setCustomEditorOpen(true)}
          />

          <SettingsBlock settings={settings} onChange={setSettings} />
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 p-4 md:p-5 border-t border-slate-200 bg-white md:rounded-b-3xl flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2.5 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-100"
            data-testid="template-editor-cancel"
          >
            Peruuta
          </button>
          <button
            onClick={save}
            disabled={saving || !name.trim()}
            className="px-6 py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold disabled:opacity-50 shadow-sm"
            data-testid="template-editor-save"
          >
            {saving ? "Tallennetaan…" : isNew ? "Luo työpohja" : "Tallenna muutokset"}
          </button>
        </div>
      </div>

      {/* Nested custom fields editor */}
      {customEditorOpen && (
        <CustomFieldsDialog
          report={{ custom_fields: customFields }}
          onClose={() => setCustomEditorOpen(false)}
          onSaved={async (updated) => {
            setCustomFields(updated);
          }}
        />
      )}
    </div>
  );
}

/* ============================================================================
 * BuildingBlock — geneerinen "rakennuspalikka" työvaiheille & havainnoille.
 * Aina näkyvillä: otsikko + progress + itemit + "+ Lisää" -nappi.
 * Input-kenttä näkyy vasta kun käyttäjä klikkaa "+ Lisää"-nappia.
 * ============================================================================ */
function BuildingBlock({ accent, icon: Icon, title, subtitle, emptyLabel, countLabel, items, onChange, placeholder, addLabel, testId }) {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");
  const [editingIndex, setEditingIndex] = useState(-1);

  const accentIconClass = {
    emerald: "bg-emerald-50 text-emerald-600 ring-emerald-100",
    amber: "bg-amber-50 text-amber-600 ring-amber-100",
    violet: "bg-violet-50 text-violet-600 ring-violet-100",
    sky: "bg-sky-50 text-sky-600 ring-sky-100",
  }[accent] || "bg-slate-50 text-slate-600 ring-slate-100";

  const accentAddClass = {
    emerald: "text-emerald-700 hover:bg-emerald-50",
    amber: "text-amber-700 hover:bg-amber-50",
    violet: "text-violet-700 hover:bg-violet-50",
    sky: "text-sky-700 hover:bg-sky-50",
  }[accent] || "text-slate-700 hover:bg-slate-100";

  const commit = () => {
    const v = draft.trim();
    if (!v) { setAdding(false); setDraft(""); return; }
    onChange([...items, v]);
    setDraft("");
    // Jatka lisäämään — käyttäjä voi lisätä useita peräkkäin
  };

  const cancelAdd = () => { setAdding(false); setDraft(""); };
  const removeAt = (i) => onChange(items.filter((_, idx) => idx !== i));
  const editAt = (i, v) => onChange(items.map((s, idx) => (idx === i ? v : s)));

  return (
    <section className="rounded-2xl bg-white border border-slate-200 p-5 md:p-6" data-testid={`template-block-${testId}`}>
      {/* Header */}
      <header className="flex items-start gap-3 mb-4">
        <div className={`inline-flex h-10 w-10 items-center justify-center rounded-xl ring-1 flex-shrink-0 ${accentIconClass}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display text-base md:text-lg font-semibold text-slate-900">{title}</div>
          <div className="text-xs md:text-sm text-slate-500 mt-0.5 leading-relaxed">{subtitle}</div>
        </div>
        <ProgressPill count={items.length} label={countLabel(items.length)} emptyLabel={emptyLabel} />
      </header>

      {/* Items list */}
      {items.length > 0 && (
        <div className="space-y-1.5 mb-3" data-testid={`template-${testId}-list`}>
          {items.map((item, i) => (
            <ItemRow
              key={i}
              value={item}
              index={i}
              editing={editingIndex === i}
              onStartEdit={() => setEditingIndex(i)}
              onFinishEdit={(v) => { editAt(i, v); setEditingIndex(-1); }}
              onCancel={() => setEditingIndex(-1)}
              onRemove={() => removeAt(i)}
              testIdBase={`template-${testId}`}
            />
          ))}
        </div>
      )}

      {/* Adder — inline input tai nappi */}
      {adding ? (
        <div className="flex items-center gap-2" data-testid={`template-${testId}-adder`}>
          <input
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); commit(); }
              if (e.key === "Escape") { e.preventDefault(); cancelAdd(); }
            }}
            placeholder={placeholder}
            autoFocus
            className="flex-1 px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-slate-400 text-sm outline-none"
            data-testid={`template-${testId}-draft`}
          />
          <button
            type="button"
            onClick={commit}
            disabled={!draft.trim()}
            className="inline-flex items-center justify-center h-11 w-11 rounded-xl bg-slate-900 hover:bg-slate-800 text-white disabled:opacity-40 flex-shrink-0"
            data-testid={`template-${testId}-add`}
            aria-label="Lisää"
          >
            <Check className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={cancelAdd}
            className="inline-flex items-center justify-center h-11 w-11 rounded-xl text-slate-400 hover:bg-slate-100 flex-shrink-0"
            data-testid={`template-${testId}-cancel`}
            aria-label="Peruuta"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold transition-colors ${accentAddClass}`}
          data-testid={`template-${testId}-open-add`}
        >
          <Plus className="h-4 w-4" />
          {addLabel}
        </button>
      )}
    </section>
  );
}

/* ============================================================================
 * ItemRow — yksittäinen lista-item, klikattava → muokkaus inline.
 * Poistonappi näkyy hoverilla desktopilla, aina mobiilissa.
 * ============================================================================ */
function ItemRow({ value, editing, onStartEdit, onFinishEdit, onCancel, onRemove, testIdBase, index }) {
  const [draft, setDraft] = useState(value);

  if (editing) {
    return (
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); onFinishEdit(draft); }
            if (e.key === "Escape") { e.preventDefault(); onCancel(); }
          }}
          onBlur={() => onFinishEdit(draft)}
          autoFocus
          className="flex-1 px-4 py-2.5 rounded-xl border-2 border-slate-300 text-sm outline-none focus:border-slate-500"
          data-testid={`${testIdBase}-item-${index}-input`}
        />
      </div>
    );
  }

  return (
    <div
      className="group flex items-center gap-2 px-4 py-2.5 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors cursor-text"
      onClick={() => { setDraft(value); onStartEdit(); }}
      data-testid={`${testIdBase}-item-${index}`}
    >
      <div className="flex-1 min-w-0 text-sm text-slate-800 truncate">{value}</div>
      <PencilLine className="h-3.5 w-3.5 text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity" />
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onRemove(); }}
        className="p-1.5 rounded-lg text-slate-300 hover:text-red-600 hover:bg-white opacity-70 md:opacity-0 md:group-hover:opacity-100 transition-opacity"
        data-testid={`${testIdBase}-item-${index}-remove`}
        aria-label="Poista"
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

/* ============================================================================
 * ProgressPill — otsikkopalkin edistymismerkintä.
 * ============================================================================ */
function ProgressPill({ count, label, emptyLabel }) {
  if (count === 0) {
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-100 text-slate-400 text-[11px] font-semibold whitespace-nowrap">
        {emptyLabel}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-semibold whitespace-nowrap ring-1 ring-emerald-100">
      <Check className="h-3 w-3" />
      {label}
    </span>
  );
}

/* ============================================================================
 * MaterialsBlock — sama kuvio kuin BuildingBlock, mutta 2 kenttää per item.
 * ============================================================================ */
function MaterialsBlock({ materials, onChange }) {
  const [adding, setAdding] = useState(false);
  const [draftName, setDraftName] = useState("");
  const [draftUnit, setDraftUnit] = useState("kpl");
  const [editingIndex, setEditingIndex] = useState(-1);

  const commit = () => {
    const n = draftName.trim();
    if (!n) { setAdding(false); setDraftName(""); return; }
    onChange([...materials, { name: n, unit: draftUnit.trim() || "kpl" }]);
    setDraftName("");
    setDraftUnit("kpl");
  };

  const cancelAdd = () => { setAdding(false); setDraftName(""); setDraftUnit("kpl"); };
  const remove = (i) => onChange(materials.filter((_, idx) => idx !== i));
  const edit = (i, patch) => onChange(materials.map((m, idx) => (idx === i ? { ...m, ...patch } : m)));

  return (
    <section className="rounded-2xl bg-white border border-slate-200 p-5 md:p-6" data-testid="template-block-materials">
      <header className="flex items-start gap-3 mb-4">
        <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl ring-1 bg-violet-50 text-violet-600 ring-violet-100 flex-shrink-0">
          <Package className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display text-base md:text-lg font-semibold text-slate-900">Vakiotarvikkeet</div>
          <div className="text-xs md:text-sm text-slate-500 mt-0.5 leading-relaxed">
            Yleisimmin käytettävät tarvikkeet — asentaja lisää ne yhdellä painalluksella työmaalla.
          </div>
        </div>
        <ProgressPill
          count={materials.length}
          label={`${materials.length} ${materials.length === 1 ? "tarvike" : "tarviketta"}`}
          emptyLabel="Ei tarvikkeita vielä"
        />
      </header>

      {materials.length > 0 && (
        <div className="space-y-1.5 mb-3" data-testid="template-materials-list">
          {materials.map((m, i) => (
            <MaterialRow
              key={i}
              material={m}
              index={i}
              editing={editingIndex === i}
              onStartEdit={() => setEditingIndex(i)}
              onFinishEdit={() => setEditingIndex(-1)}
              onChange={(patch) => edit(i, patch)}
              onRemove={() => remove(i)}
            />
          ))}
        </div>
      )}

      {adding ? (
        <div className="flex items-center gap-2" data-testid="template-mat-adder">
          <input
            type="text"
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); commit(); }
              if (e.key === "Escape") { e.preventDefault(); cancelAdd(); }
            }}
            placeholder="esim. F7-suodatin"
            autoFocus
            className="flex-1 px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-slate-400 text-sm outline-none"
            data-testid="template-mat-draft-name"
          />
          <input
            type="text"
            value={draftUnit}
            onChange={(e) => setDraftUnit(e.target.value)}
            placeholder="kpl"
            className="w-20 px-3 py-3 rounded-xl border-2 border-slate-200 focus:border-slate-400 text-sm outline-none text-center"
            data-testid="template-mat-draft-unit"
          />
          <button
            type="button"
            onClick={commit}
            disabled={!draftName.trim()}
            className="inline-flex items-center justify-center h-11 w-11 rounded-xl bg-slate-900 hover:bg-slate-800 text-white disabled:opacity-40 flex-shrink-0"
            data-testid="template-mat-add"
            aria-label="Lisää"
          >
            <Check className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={cancelAdd}
            className="inline-flex items-center justify-center h-11 w-11 rounded-xl text-slate-400 hover:bg-slate-100 flex-shrink-0"
            aria-label="Peruuta"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold text-violet-700 hover:bg-violet-50 transition-colors"
          data-testid="template-mat-open-add"
        >
          <Plus className="h-4 w-4" />
          Lisää tarvike
        </button>
      )}
    </section>
  );
}

function MaterialRow({ material, editing, onStartEdit, onFinishEdit, onChange, onRemove, index }) {
  if (editing) {
    return (
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={material.name}
          onChange={(e) => onChange({ name: e.target.value })}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onFinishEdit(); } }}
          onBlur={onFinishEdit}
          autoFocus
          className="flex-1 px-4 py-2.5 rounded-xl border-2 border-slate-300 text-sm outline-none focus:border-slate-500"
          data-testid={`template-mat-name-${index}`}
        />
        <input
          type="text"
          value={material.unit}
          onChange={(e) => onChange({ unit: e.target.value })}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onFinishEdit(); } }}
          onBlur={onFinishEdit}
          className="w-20 px-3 py-2.5 rounded-xl border-2 border-slate-300 text-sm outline-none focus:border-slate-500 text-center"
          data-testid={`template-mat-unit-${index}`}
        />
      </div>
    );
  }
  return (
    <div
      className="group flex items-center gap-2 px-4 py-2.5 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors cursor-text"
      onClick={onStartEdit}
      data-testid={`template-mat-row-${index}`}
    >
      <div className="flex-1 min-w-0 text-sm text-slate-800 truncate">{material.name}</div>
      <span className="text-xs text-slate-400 whitespace-nowrap">{material.unit}</span>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onRemove(); }}
        className="p-1.5 rounded-lg text-slate-300 hover:text-red-600 hover:bg-white opacity-70 md:opacity-0 md:group-hover:opacity-100 transition-opacity"
        data-testid={`template-mat-remove-${index}`}
        aria-label="Poista"
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

/* ============================================================================
 * CustomFieldsBlock — CustomFieldsDialog:n avaava kortti.
 * ============================================================================ */
function CustomFieldsBlock({ fields, onOpen }) {
  return (
    <section className="rounded-2xl bg-white border border-slate-200 p-5 md:p-6" data-testid="template-block-custom">
      <header className="flex items-start gap-3 mb-4">
        <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl ring-1 bg-sky-50 text-sky-600 ring-sky-100 flex-shrink-0">
          <FileText className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display text-base md:text-lg font-semibold text-slate-900">Muut kerättävät tiedot</div>
          <div className="text-xs md:text-sm text-slate-500 mt-0.5 leading-relaxed">
            Yrityskohtaiset kentät, esim. Kesäpellin asento, Käyttötunnit tai Huoltoluokka.
          </div>
        </div>
        <ProgressPill
          count={fields.length}
          label={`${fields.length} ${fields.length === 1 ? "kenttä" : "kenttää"}`}
          emptyLabel="Ei kenttiä vielä"
        />
      </header>

      {fields.length > 0 && (
        <div className="space-y-1.5 mb-3">
          {fields.map((cf) => (
            <div
              key={cf.id}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-slate-50 text-sm"
              data-testid={`template-custom-preview-${cf.id}`}
            >
              <span className="flex-1 text-slate-800 truncate">{cf.label || "Kenttä"}</span>
              <span className="text-[11px] text-slate-400 uppercase tracking-wider">
                {cf.type === "select" ? `Valinta · ${(cf.options || []).length}` : cf.type === "number" ? "Numero" : "Teksti"}
              </span>
            </div>
          ))}
        </div>
      )}

      <button
        type="button"
        onClick={onOpen}
        className="w-full inline-flex items-center justify-center gap-2 px-3 py-3 rounded-xl text-sm font-semibold text-sky-700 bg-sky-50 hover:bg-sky-100 transition-colors border-2 border-dashed border-sky-200 hover:border-sky-300"
        data-testid="template-custom-open"
      >
        <Plus className="h-4 w-4" />
        {fields.length === 0 ? "Lisää ensimmäinen kenttä" : `Lisää tai muokkaa kenttiä (${fields.length})`}
      </button>
    </section>
  );
}


/* ============================================================================
 * SettingsBlock — työpohjan raportti-asetukset "täppinä". Nämä samat rukselit
 * ovat myös raportin luontivaiheessa; kun tallennettu työpohjaan, uudet
 * raportit periytyvät automaattisesti (myös FieldHomen "Aloita työ").
 * ========================================================================== */
function SettingsBlock({ settings, onChange }) {
  const set = (key, val) => onChange({ ...settings, [key]: val });
  const defaults = {
    image_timestamps: true, request_signature: true, ask_executor: false,
    hide_work_checklists: false, hide_finding_checklists: false,
    hide_materials: false, hide_kpi_cards: true,
  };
  const activeCount = Object.entries(settings).filter(([k, v]) => v !== defaults[k]).length;

  const options = [
    { key: "image_timestamps", icon: Clock, label: "Näytä kuvien aikaleima raportissa", hint: "Näyttää kuvan alle päivämäärän ja kellonajan." },
    { key: "request_signature", icon: PenTool, label: "Pyydä allekirjoitus valmistuessa", hint: "Kentältä aloitetuille töille suositellaan päälle. Ohitettavissa." },
    { key: "ask_executor", icon: User2, label: "Kysy suorittajan nimeä yksikkötasolla", hint: "Näytä 'Suorittaja'-kenttä jokaisessa yksikössä." },
    { key: "hide_work_checklists", icon: EyeOff, label: "Piilota työvaihelistat asiakkaan raportilta", hint: "Vain toimisto näkee. PDF ei sisällä työvaiherukseja." },
    { key: "hide_finding_checklists", icon: EyeOff, label: "Piilota havaintolistat asiakkaan raportilta", hint: "Näytetään vain jos jotain on havaittu." },
    { key: "hide_materials", icon: EyeOff, label: "Piilota tarvikkeet asiakkaan raportilta", hint: "PDF ei näytä käytettyjä tarvikkeita." },
    { key: "hide_kpi_cards", icon: Eye, label: "Piilota KPI-mittarikortit raportista", hint: "Poistaa yhteenveto-kortit raportin ylälaidasta." },
  ];

  return (
    <section className="rounded-2xl bg-white border border-slate-200 p-5 md:p-6" data-testid="template-block-settings">
      <header className="flex items-start gap-3 mb-4">
        <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl ring-1 bg-slate-50 text-slate-600 ring-slate-200 flex-shrink-0">
          <Settings2 className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display text-base md:text-lg font-semibold text-slate-900">Raporttiasetukset</div>
          <div className="text-xs md:text-sm text-slate-500 mt-0.5 leading-relaxed">
            Nämä valinnat periytyvät automaattisesti uusiin raportteihin — säädä kerran ja unohda.
          </div>
        </div>
        <ProgressPill
          count={activeCount}
          label={`${activeCount} mukautettu`}
          emptyLabel="Oletusasetukset"
        />
      </header>

      <div className="space-y-2">
        {options.map((o) => (
          <SettingToggle
            key={o.key}
            icon={o.icon}
            label={o.label}
            hint={o.hint}
            checked={!!settings[o.key]}
            onChange={(v) => set(o.key, v)}
            testId={`template-setting-${o.key}`}
          />
        ))}
      </div>
    </section>
  );
}

function SettingToggle({ icon: Icon, label, hint, checked, onChange, testId }) {
  return (
    <label
      className={`flex items-start gap-3 rounded-xl border p-3 cursor-pointer transition-colors ${
        checked ? "border-emerald-200 bg-emerald-50/40" : "border-slate-200 bg-white hover:bg-slate-50"
      }`}
      data-testid={testId}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 focus:ring-2 flex-shrink-0"
      />
      <div className={`h-8 w-8 rounded-lg flex items-center justify-center flex-shrink-0 ${checked ? "bg-emerald-100 text-emerald-600" : "bg-slate-100 text-slate-400"}`}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className={`text-sm font-semibold leading-tight ${checked ? "text-slate-900" : "text-slate-700"}`}>{label}</div>
        <div className="text-xs text-slate-500 mt-0.5 leading-snug">{hint}</div>
      </div>
    </label>
  );
}
